import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL, ApiError, apiFetch } from '../api.js';
import { formatDay } from '../format.js';
import { PasswordInput } from './PasswordInput.js';

interface ShortcutKey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

const KEY = ['shortcuts'] as const;

/** Absolute Adresse der API — Kurzbefehle laufen außerhalb der App. */
function apiBase(): string {
  return new URL(API_URL, window.location.origin).toString().replace(/\/$/, '');
}

/**
 * iOS-Kurzbefehle: Gewicht eintragen und Supplements abhaken, ohne die App zu
 * öffnen („Hey Siri, Gewicht"). Je Kurzbefehl ein Schlüssel, der **nur** diese
 * zwei Dinge kann; einrichten mit Passwort, widerrufen hier.
 */
export function ShortcutSetting(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const nameId = useId();
  const passwordId = useId();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const list = useQuery({
    queryKey: KEY,
    queryFn: async () => (await apiFetch<{ tokens: ShortcutKey[] }>('/shortcuts')).tokens,
  });
  const create = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string; token: string }>('/shortcuts', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim() || 'iPhone', password }),
      }),
    onSuccess: async (result) => {
      setFresh(result.token);
      setAdding(false);
      setPassword('');
      setName('');
      await queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => apiFetch<undefined>(`/shortcuts/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });

  const base = apiBase();
  const status = create.error instanceof ApiError ? create.error.status : null;

  return (
    <section aria-labelledby="settings-shortcuts" className="kw-tile flex flex-col gap-2">
      <h3 id="settings-shortcuts" className="kw-kicker">
        {t('shortcuts.heading')}
      </h3>
      <p className="text-sm text-ink-muted">{t('shortcuts.intro')}</p>

      {list.isError ? (
        <p className="text-xs text-ink-muted">{t('passkeys.offline')}</p>
      ) : list.data && list.data.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {list.data.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-2 rounded-xl border border-line px-3 py-2 text-sm"
            >
              <span className="min-w-0">
                <span className="block truncate font-semibold">{entry.name}</span>
                <span className="block text-xs text-ink-muted">
                  {entry.lastUsedAt
                    ? t('shortcuts.lastUsed', {
                        date: formatDay(entry.lastUsedAt.slice(0, 10), 'date', i18n.language),
                      })
                    : t('shortcuts.neverUsed')}
                </span>
              </span>
              <button
                type="button"
                onClick={() => revoke.mutate(entry.id)}
                aria-label={t('shortcuts.revokeNamed', { name: entry.name })}
                className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 text-danger"
              >
                {t('shortcuts.revoke')}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {fresh ? (
        <div role="status" className="flex flex-col gap-2 rounded-xl border border-good p-3">
          <p className="text-sm font-semibold">{t('shortcuts.onlyOnce')}</p>
          <code className="block break-all rounded-lg bg-surface-raised p-2 text-xs">{fresh}</code>
          <button
            type="button"
            onClick={() =>
              void navigator.clipboard?.writeText(fresh).then(
                () => setCopied(true),
                () => setCopied(false),
              )
            }
            className="kw-btn kw-btn-small"
          >
            {copied ? t('shortcuts.copied') : t('shortcuts.copy')}
          </button>
          <ol className="flex list-decimal flex-col gap-1 pl-5 text-xs">
            <li>{t('shortcuts.step1')}</li>
            <li>{t('shortcuts.step2', { url: `${base}/quick/weight` })}</li>
            <li>{t('shortcuts.step3')}</li>
            <li>{t('shortcuts.step4')}</li>
            <li>{t('shortcuts.step5', { url: `${base}/quick/taken` })}</li>
          </ol>
          <p className="text-xs font-semibold">{t('shortcuts.healthHeading')}</p>
          <ol className="flex list-decimal flex-col gap-1 pl-5 text-xs">
            <li>{t('shortcuts.health1')}</li>
            <li>{t('shortcuts.health2')}</li>
            <li>{t('shortcuts.health3', { url: `${base}/quick/steps` })}</li>
            <li>{t('shortcuts.health4', { url: `${base}/quick/weight` })}</li>
          </ol>
          <p className="text-xs text-ink-muted">{t('shortcuts.healthNote')}</p>
          <button
            type="button"
            onClick={() => {
              setFresh(null);
              setCopied(false);
            }}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('shortcuts.done')}
          </button>
        </div>
      ) : adding ? (
        <form
          className="flex flex-col gap-2 rounded-xl border border-line p-3"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <label htmlFor={nameId} className="flex flex-col gap-1">
            <span className="kw-label">{t('shortcuts.name')}</span>
            <input
              id={nameId}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('passkeys.namePlaceholder')}
              maxLength={60}
              className="kw-field"
            />
          </label>
          <label htmlFor={passwordId} className="flex flex-col gap-1">
            <span className="kw-label">{t('passkeys.password')}</span>
            <PasswordInput
              id={passwordId}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="kw-field"
              required
            />
          </label>
          {create.isError ? (
            <p role="alert" className="text-sm text-danger">
              {status === 403 ? t('settings.delete.wrongPassword') : t('passkeys.failed')}
            </p>
          ) : null}
          <button type="submit" disabled={create.isPending} className="kw-btn kw-btn-small">
            {t('shortcuts.create')}
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setPassword('');
              create.reset();
            }}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('nutrition.library.cancel')}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('shortcuts.add')}
        </button>
      )}
    </section>
  );
}
